import React, {ReactElement} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getCrumbsFromListed, ListedCrumbItem} from 'nav/Breadcrumbs';
import SidepanelHeader from 'nav/sidepanel/SidepanelHeader';
import TFIAccountIcon from 'notebook/tfi/TFIAccountIcon';
import TFIActions from 'notebook/tfi/TFIActions';
import TFIAddAccountModal from 'notebook/tfi/TFIAddAccountModal';
import {TFIAccount, TFIFolder, TFIObject} from 'notebook/tfi/TFIConstants';
import TFIUtil from 'notebook/tfi/TFIUtil';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ModalAnchor from 'ui-library/ModalAnchor';
import * as nyc from 'ui-library/NYC';

import SearchButton from '../SearchButton';
import TFIAccountList from './TFIAccountList';
import TFIObjectList from './TFIObjectList';
import TFISearchInput from './TFISearchInput';
import TFISettingsIcon from './TFISettingsIcon';

export interface TFISidepanelInterface {
  accounts?: Array<TFIAccount> | null;
  listedFolder?: TFIFolder | null;
  search: {
    query?: string | null;
    isActive: boolean;
    tfiData: {
      isSearching: boolean;
      results: Array<TFIObject>;
    };
  };
  showTFIAddModal: boolean;
}
export interface Props extends TFISidepanelInterface {
  connectionsTab: JSX.Element;
}

interface State {
  searchInputPendingFocus: boolean;
}

class TFISidepanel extends React.Component<Props, State> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static getListed(listedFolder: any): ListedCrumbItem | null {
    // Convert TFI `listedFolder` to the `listed` folder format that `Breadcrumbs` expects.
    const listed = {};
    let curFolder = listed;
    let curTFIFolder = listedFolder;
    if (!curTFIFolder) {
      return null;
    }
    while (curTFIFolder) {
      assert(curTFIFolder.isFolder, 'TFI file cannot be a parent of TFI folder');
      Object.assign(
        curFolder,
        curTFIFolder,
        {api_identifier: curTFIFolder.id},
        curTFIFolder.parent
          ? {name: curTFIFolder.name, parent: {}}
          : {name: TFIUtil.readableAccountType(curTFIFolder.accountType), parent: null}
      );
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'parent' does not exist on type '{}'.
      curFolder = curFolder.parent;
      curTFIFolder = curTFIFolder.parent;
    }
    return listed as ListedCrumbItem;
  }

  state: State = {
    searchInputPendingFocus: false,
  };

  componentDidMount(): void {
    TFIActions.fetchAccounts();
  }

  private handleTFIAddModalOpen = (): void => {
    TFIActions.toggleTFIAddModal(true);
  };

  private handleTFIAddModalClose = (): void => {
    TFIActions.toggleTFIAddModal(false);
  };

  private handleGoToRoot = (): void => {
    TFIActions.listAccounts();
  };

  private handleTriggerTFISearch = (): void => {
    TFIActions.setQuery('');
    this.setState({searchInputPendingFocus: true});
  };

  private handleSearchInputReceivedFocus = (): void => {
    this.setState({searchInputPendingFocus: false});
  };

  private handleOpenResult = (tfiObject: TFIObject): void => {
    TFIActions.listFolder(tfiObject.accountId, tfiObject.id);
  };

  private renderTFIIcon = (tfiObject: ListedCrumbItem & TFIObject): JSX.Element => {
    if (!tfiObject) {
      return <BenchlingIcon icon="external-files" />;
    }
    if (!tfiObject.parent) {
      return <TFIAccountIcon accountType={tfiObject.accountType} />;
    }
    return <BenchlingIcon icon={ModelTypeConstants.FOLDER} />;
  };

  getCrumbs = (): Array<ReactElement> =>
    getCrumbsFromListed({
      listed: TFISidepanel.getListed(this.props.listedFolder)!,
      openResult: this.handleOpenResult,
      onGoToRoot: this.handleGoToRoot,
      rootText: BenchlingConfig.ENABLE_BENCHLING_APPS ? 'Connections' : 'External Files',
      renderIcon: this.renderTFIIcon,
    });

  renderTFIObjects = (): React.ReactNode => {
    if (this.props.search.isActive) {
      return (
        <TFIObjectList
          tfiObjects={this.props.search.tfiData.isSearching ? null : this.props.search.tfiData.results}
        />
      );
    }
    if (this.props.listedFolder) {
      return [
        <nyc.Row key="sidepanel-header-row">
          <nyc.Col sm={6}>
            <div className="sidepanel-header">
              <SidepanelHeader
                crumbs={this.getCrumbs()}
                headerButtons={<SearchButton onClick={this.handleTriggerTFISearch} />}
                compact={true}
                rootText={BenchlingConfig.ENABLE_BENCHLING_APPS ? 'Connections' : 'External Files'}
                onGoToRoot={this.handleGoToRoot}
              />
            </div>
          </nyc.Col>
        </nyc.Row>,
        <div key="tab-container">{this.props.connectionsTab}</div>,
        <TFIObjectList key="folder-listing" tfiObjects={this.props.listedFolder.children} />,
      ];
    }
    return [
      <nyc.Row className="u-position-relative" key="sidepanel-header-row">
        <nyc.Col sm={6}>
          <div className="sidepanel-header">
            <SidepanelHeader
              crumbs={this.getCrumbs()}
              compact={true}
              headerButtons={
                <div className="sidepanel-header-buttons u-vertical-center-items">
                  <SearchButton onClick={this.handleTriggerTFISearch} />
                  <Tooltip title="Add Account" placement={TooltipPlacement.BOTTOM} container="body">
                    <Button
                      buttonStyle={ButtonStyle.TERTIARY_BLUE}
                      icon="compose"
                      onClick={this.handleTFIAddModalOpen}
                      size={ButtonSize.SMALL}
                    />
                  </Tooltip>
                </div>
              }
              settingsIcon={<TFISettingsIcon accounts={this.props.accounts} />}
              rootText={BenchlingConfig.ENABLE_BENCHLING_APPS ? 'Connections' : 'External Files'}
              onGoToRoot={this.handleGoToRoot}
            />
          </div>
        </nyc.Col>
      </nyc.Row>,
      <div key="tab-container">{this.props.connectionsTab}</div>,
      <TFIAccountList key="account-list" accounts={this.props.accounts} />,

      <ModalAnchor key="add-account-modal-anchor" open={this.props.showTFIAddModal}>
        <TFIAddAccountModal tfiAccounts={this.props.accounts} onRequestClose={this.handleTFIAddModalClose} />
      </ModalAnchor>,
    ];
  };

  render(): JSX.Element {
    return (
      <nyc.Container
        className="sidepanel"
        size="sidepanel"
        dataTestAttrs={{component: 'TFISidepanel', element: 'root', key: 'collapsed'}}
      >
        <nyc.Row>
          <nyc.Col sm={6} className="sidepanel-search">
            {this.props.search.isActive && (
              <TFISearchInput
                accounts={this.props.accounts}
                listedFolder={this.props.listedFolder}
                onSearchInputReceivedFocus={this.handleSearchInputReceivedFocus}
                query={this.props.search.query}
                searchInputPendingFocus={this.state.searchInputPendingFocus}
              />
            )}
          </nyc.Col>
        </nyc.Row>
        {this.renderTFIObjects()}
      </nyc.Container>
    );
  }
}

export default TFISidepanel;
